import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import queryString from 'query-string';
import { LocalizationProvider } from '@vp/om-translations';
import TrackingJS from '@vp/react-tracking';
import { UserFeatureProvider } from '@vp/core-ui-components';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  BoundedContent,
  MainContentStart,
  ScreenClassProvider,
  SiteContent,
  SiteMain,
} from '@vp/swan';
import { InitializeNewRelic } from '../components/newrelic/NewRelicUtilities';
import { SiteProvider } from '../components/contexts/SiteContext';
import { OrderDetailsSession } from '../components/orderdetails/OrderDetailsSession';
import translationResources from '../../assets/i18n';
import { IdentityProvider } from '../components/contexts/IdentityContext';
import { DebugModeProvider } from '../components/contexts/DebugModeContext';
import { SourcePageProvider } from '../components/contexts/SourcePageContext';
import { PageMetadata } from '../components/shared/PageMetadata';
import { PageModsProvider } from '../components/contexts/PageModsContext';
import { TaxDisplayProvider } from '../components/shared/taxDisplay/TaxDisplayContext';
import { TRACKING_LIB_VERSION } from '../constants/tracking';
import {
  BookendsFooter,
  BookendsHeader,
} from '../components/shared/bookends/Bookends';
import { QUERY_DEFAULT_OPTIONS } from '../constants/reactQueryDefaultConfig';
import {
  ORDER_CONFIRMATION_PAGE_NAME,
  ORDER_DETAILS_PAGE_NAME,
} from '../constants/orderdetailsui';
import { LineItemActionOverrideProvider } from '../components/LineItemActionOverride';
import { SwanEnabler } from '../components/shared/SwanEnabler';
import { ExperimentsProvider } from '../experimentation/ExperimentsContext';
import { getBookendsType } from '../util/bookendsUtils';
import { DesignFeedbackGroupSession } from '../components/orderdetails/designFeedback/DesignFeedbackGroupSession';

const designFeedbackActionKey = 'df';

// Create a react-query client
const queryClient = new QueryClient();
queryClient.setDefaultOptions(QUERY_DEFAULT_OPTIONS);

const Session = ({
  orderId,
  groupId,
  lineItemId,
  confirmationMode,
  action,
  isGuest,
}) => {
  if (action === designFeedbackActionKey && groupId) {
    return <DesignFeedbackGroupSession orderId={orderId} groupId={groupId} />;
  }
  return (
    <OrderDetailsSession
      orderId={orderId}
      lineItemId={lineItemId}
      confirmationMode={confirmationMode}
      isGuest={isGuest}
    />
  );
};
Session.propTypes = {
  orderId: PropTypes.string,
  groupId: PropTypes.string,
  lineItemId: PropTypes.string,
  confirmationMode: PropTypes.bool,
  action: PropTypes.string,
  isGuest: PropTypes.bool,
};

function OrderDetails({ data: { stcSites: siteData }, location: { search } }) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const queryStringKeyValueMap = queryString.parse(search);
  const { confirm, orderId, lineItemId, groupId, action } =
    queryStringKeyValueMap;

  // checks that `isGuest` param exists in the query param and is not set `false`
  const showGuestOrderNumber = () => {
    if (!queryStringKeyValueMap) {
      return false;
    }
    return (
      !!Object.keys(queryStringKeyValueMap).includes('isGuest') &&
      queryStringKeyValueMap.isGuest !== 'false'
    );
  };

  const {
    orderDetails: { uxMods, auth, bookends },
    pricingDisplay,
  } = siteData.uiData;

  const { featureControls } = siteData;

  const bookendsConfig = {
    ...bookends,
    headerType: getBookendsType(bookends?.headerType),
    footerType: getBookendsType(bookends?.footerType),
  };

  const { taxDisplayMode } = pricingDisplay;
  const rawSiteUxMods = uxMods || [];

  const {
    brand: trackingTenant,
    environment: trackingEnvironment,
    culture: trackingCulture,
  } = siteData.tracking;
  const { locale, language } = siteData.uiLocalization;

  useEffect(() => {
    const isConfirmation = confirm === null;
    setShowConfirmation(isConfirmation);
  }, [confirm, orderId]);

  // because gatsby can't pass through objects with 'id' properties, our source json uses 'modId'.
  // we need to map those modRefs into the format that mod-father requires.
  const siteConfigPageUxMods = (rawSiteUxMods || []).map(
    ({ modRef: { family, modId }, scope }) => ({
      modRef: {
        family,
        id: modId,
      },
      scope,
      enabled: true,
    })
  );

  return (
    <>
      <TrackingJS
        tenant={trackingTenant}
        environment={trackingEnvironment}
        culture={trackingCulture}
        version={TRACKING_LIB_VERSION}
        suppressReloadOnConsentSubmit
      />
      <InitializeNewRelic />
      <SwanEnabler>
        <ScreenClassProvider>
          <SiteProvider value={{ ...siteData, auth }}>
            <SourcePageProvider override isConfirmation={confirm === null}>
              <IdentityProvider>
                <SiteContent className="ui-version-3 om-order-details">
                  <BookendsHeader
                    bookendsConfig={bookendsConfig}
                    pageName={
                      showConfirmation
                        ? ORDER_CONFIRMATION_PAGE_NAME
                        : ORDER_DETAILS_PAGE_NAME
                    }
                  />
                  <SiteMain>
                    <MainContentStart />
                    <LocalizationProvider
                      translationResources={translationResources}
                      locale={locale}
                      logLevel="ERROR"
                    >
                      <ExperimentsProvider>
                        <DebugModeProvider>
                          <PageModsProvider
                            siteConfigPageUxMods={siteConfigPageUxMods}
                          >
                            <PageMetadata
                              metadataKey="orderDetailsPageMetadata"
                              language={language}
                              bookendsConfig={bookendsConfig}
                            />
                            <TaxDisplayProvider taxDisplayMode={taxDisplayMode}>
                              <UserFeatureProvider
                                featureControls={featureControls}
                              >
                                <LineItemActionOverrideProvider>
                                  <QueryClientProvider client={queryClient}>
                                    <BoundedContent mb="between-sections">
                                      <Session
                                        orderId={orderId}
                                        groupId={groupId}
                                        lineItemId={lineItemId}
                                        confirmationMode={showConfirmation}
                                        action={action}
                                        isGuest={showGuestOrderNumber()}
                                      />
                                    </BoundedContent>
                                  </QueryClientProvider>
                                </LineItemActionOverrideProvider>
                              </UserFeatureProvider>
                            </TaxDisplayProvider>
                          </PageModsProvider>
                        </DebugModeProvider>
                      </ExperimentsProvider>
                    </LocalizationProvider>
                  </SiteMain>
                  <BookendsFooter
                    bookendsConfig={bookendsConfig}
                    pageName={
                      showConfirmation
                        ? ORDER_CONFIRMATION_PAGE_NAME
                        : ORDER_DETAILS_PAGE_NAME
                    }
                  />
                </SiteContent>
              </IdentityProvider>
            </SourcePageProvider>
          </SiteProvider>
        </ScreenClassProvider>
      </SwanEnabler>
    </>
  );
}

export const query = graphql`
  query ($siteName: String!) {
    stcSites(siteName: { eq: $siteName }) {
      siteName
      country
      siteBaseUrl
      vistacartTenantName
      tenantEnvironment
      tracking {
        brand
        country
        environment
        culture
      }
      uiData {
        pricingDisplay {
          taxDisplayMode
        }
        orderDetails {
          uxMods {
            modRef {
              family
              modId
            }
            scope
          }
          bookends {
            tenant
            locale
            headerType
            footerType
            hideSearch
            hideVat
            hideCart
          }
          reorderEnabled
          auth {
            allowAnonymous
            site
          }
          customerShippingAddressEditEnabled
          printerFriendlyEnabled
          postOrderStudioEnabled
          selfOrderTransferEnabled
        }
        checkout {
          addressValidation {
            shippingAddressEnabled
          }
          addressAutocomplete {
            shippingAddressEnabled
          }
        }
        addressValidation {
          loqateAPIKey
          loqateEnabledCountries
        }
        addressAutocomplete {
          provider
          apiKey
          enabledCountries
        }
      }
      uiLocalization {
        language
        locale
      }
      nav {
        myAccount
        orderHistory
        orderDetails
        customerCareContact
        helpCenter
        termsAndConditions
        myProjects
        base
        cart
        checkout
        smsPrivacy
        smsTermsAndConditions
      }
      featureControls {
        enableExpressCheckoutOnOrderDetails
        enableItemStatusTooltip
        enableImAlerts
      }
      pricingOptions {
        merchantId
      }
    }
  }
`;

OrderDetails.propTypes = {
  data: PropTypes.shape({
    stcSites: PropTypes.shape({
      siteName: PropTypes.string,
      siteBaseUrl: PropTypes.string,
      vistacartTenantName: PropTypes.string,
      tracking: PropTypes.shape({
        brand: PropTypes.string,
        country: PropTypes.string,
        environment: PropTypes.string,
        culture: PropTypes.string,
      }),
      uiLocalization: PropTypes.shape({
        locale: PropTypes.string,
        language: PropTypes.string,
      }),
      uiData: PropTypes.shape({
        pricingDisplay: PropTypes.shape({
          taxDisplayMode: PropTypes.string,
        }),
        orderDetails: PropTypes.shape({
          auth: PropTypes.object,
          uxMods: PropTypes.array,
          bookends: PropTypes.shape({
            tenant: PropTypes.string,
            locale: PropTypes.string,
            headerType: PropTypes.string,
            footerType: PropTypes.string,
            hideSearch: PropTypes.bool,
            hideVat: PropTypes.bool,
            hideCart: PropTypes.bool,
          }),
          reorderEnabled: PropTypes.bool,
        }),
        addressValidation: PropTypes.shape({
          loqateAPIKey: PropTypes.string,
          loqateEnabledCountries: PropTypes.array,
        }),
      }),
      nav: PropTypes.object,
      tenantEnvironment: PropTypes.string,
      featureControls: PropTypes.object,
    }),
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

export default OrderDetails;
